import { forwardRef, useId, type ReactNode } from 'react'
import type {
  InputHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { cn } from '@/lib/cn'

/** Props `Field` hands to its control so label, hint, and error stay wired up. */
export interface ControlProps {
  id: string
  'aria-describedby': string | undefined
  'aria-invalid': boolean | undefined
  required: boolean | undefined
}

/**
 * Every input wraps in a Field. The render-prop shape is what guarantees the
 * label/hint/error association rather than leaving it to each call site.
 */
export function Field({
  label,
  hint,
  error,
  required,
  className,
  children,
}: {
  label: string
  hint?: ReactNode
  error?: string
  required?: boolean
  className?: string
  children: (props: ControlProps) => ReactNode
}) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={id} className="field-label block">
        {label}
        {required && (
          <span className="ml-1 text-brand-red-700" aria-hidden>
            *
          </span>
        )}
      </label>
      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        required,
      })}
      {hint && !error && (
        <p id={hintId} className="text-[12px] leading-4 text-ink-faint">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-[12px] leading-4 text-brand-red-700">
          {error}
        </p>
      )}
    </div>
  )
}

/** The look shared by every text control, `Combobox`'s trigger included. */
export const CONTROL = cn(
  'w-full rounded-[4px] border border-rule-strong bg-surface px-2.5 text-[14px] text-ink',
  'placeholder:text-ink-faint transition-colors',
  'hover:border-ink-faint focus:border-brand-blue',
  'disabled:cursor-not-allowed disabled:bg-surface-sunk disabled:text-ink-faint',
  'aria-[invalid=true]:border-brand-red',
)

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cn(CONTROL, 'h-9', className)} {...rest} />
  },
)

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={cn(CONTROL, 'py-2 leading-6', className)} {...rest} />
  },
)

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select ref={ref} className={cn(CONTROL, 'h-9 cursor-pointer pr-7', className)} {...rest}>
        {children}
      </select>
    )
  },
)

export const Checkbox = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Checkbox({ className, ...rest }, ref) {
    return (
      <input
        ref={ref}
        type="checkbox"
        className={cn(
          'h-3.5 w-3.5 shrink-0 cursor-pointer rounded-[3px] border-rule-strong',
          'accent-brand-blue',
          className,
        )}
        {...rest}
      />
    )
  },
)

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'inline-flex h-5 w-9 shrink-0 items-center rounded-full px-0.5 transition-colors',
        checked ? 'bg-affirm' : 'bg-rule-strong',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <span
        className={cn(
          'h-4 w-4 rounded-full bg-white shadow-sm transition-transform',
          checked && 'translate-x-4',
        )}
        aria-hidden
      />
    </button>
  )
}
